import React, { useContext, useState } from 'react';
import AppContext from '../../../../contexts/AppContext';
import RolesContext from '../../../../contexts/RolesContext';
import { commentableContent, ICommentable } from '../../../../utils/notes';
import TinyIconButton from '../../../Shared/TinyIconButton';

const SECTIONS: Array<{ key: string; label: string }> = [
  { key: 'initial', label: 'Initial Observation' },
  { key: 'progress', label: 'Updates' },
  { key: 'delay_reason', label: 'Delay Reason' },
  { key: 'final', label: 'Final Observation' },
];

function formatHeader(
  item: ICommentable,
  showObservationDatesOfFindings?: boolean,
): string {
  const date = item.created_at
    ? new Date(item.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  const name =
    item.is_completed_by_guest && item.completed_by
      ? item.completed_by
          .split(' ')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ')
      : item.author?.name || '';
  if (!showObservationDatesOfFindings) {
    return name;
  }    
  return [date, name].filter(Boolean).join(' · ');
}

function ObservationsList({
  data,
  canEdit,
  canDelete,
  onEdit,
  onSave,
  onDelete,
  noteType = "observation",
  readOnly,
  showObservationDatesOfFindings
}: {
  data: Array<ICommentable>;
  canEdit: Boolean;
  canDelete: Boolean;
  onEdit: (data: ICommentable) => void;
  onSave?: (payload: { id: number; content: string }) => Promise<void> | void;
  onDelete: (data: ICommentable) => void;
  noteType?: string;
  readOnly?: false;
  showObservationDatesOfFindings?: boolean;
}) {
  if (!Array.isArray(data)) {
    return null;
  }

  const appStore = useContext(AppContext);
  const permCheck = useContext(RolesContext).userHasPermission;
  const userId = appStore.get('user.id');
  const isAdmin = permCheck('all', 'adminInspectionNotes');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const grouped: Record<string, ICommentable[]> = {
    initial: [],
    progress: [],
    delay_reason: [],
    final: [],
  };
  for (const item of data) {
    const type = (item.observation_type || 'progress') as string;
    (grouped[type] || grouped.progress).push(item);
  }
  Object.values(grouped).forEach((list) =>
    list.sort((a, b) => {
      const aT = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bT = b.created_at ? new Date(b.created_at).getTime() : 0;
      return aT - bT;
    }),
  );

  const handleEditClick = (val: ICommentable) => {
    if (onSave) {
      setEditingId(val.id);
      setDraft(commentableContent(val));
    } else {
      onEdit(val);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  const saveEdit = async (val: ICommentable) => {
    if (!onSave) return;
    const next = draft.trim();
    if (!next || next === commentableContent(val)) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onSave({ id: val.id, content: next });
      cancelEdit();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='observations-list'>
      {SECTIONS.map(({ key, label }) => {
        const items = grouped[key];
        if (!items.length) return null;

        return (
          <section
            className={`observations-section observations-section--${key}`}
            key={key}
          >
            <div className='observations-section__title'>{label}</div>
            <ul className='observations-section__items'>
              {items.map((val) => {
                const isOwner = val.author?.id === userId;
                const allowEdit =
                  !readOnly && ((canEdit && isOwner) || isAdmin);
                const allowDelete =
                  !readOnly && ((canDelete && isOwner) || isAdmin);
                const isEditing = editingId === val.id;

                return (
                  <li className='observations-list-item' key={val.id}>
                    <div className='observations-list-item__body'>
                      <div className='observations-list-item__header'>
                        {formatHeader(val, showObservationDatesOfFindings)}
                      </div>
                      {isEditing ? (
                        <div className='observations-list-item__editor'>
                          <textarea
                            className='form-control'
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            rows={3}
                            disabled={saving}
                          />
                          <div className='observations-list-item__editor-actions'>
                            <button
                              type='button'
                              className='button-secondary'
                              onClick={cancelEdit}
                              disabled={saving}
                            >
                              Cancel
                            </button>
                            <button
                              type='button'
                              className='button-primary'
                              onClick={() => saveEdit(val)}
                              disabled={saving}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className='observations-list-item__content'>
                          {commentableContent(val)}
                        </div>
                      )}
                    </div>
                    {!isEditing && (allowEdit || allowDelete) && (
                      <div className='observations-list-item__actions'>
                        {allowEdit && (
                          <TinyIconButton
                            iconName='pencil'
                            title={`Edit ${noteType}`}
                            onClick={() => handleEditClick(val)}
                            className=''
                          />
                        )}
                        {allowDelete && (
                          <TinyIconButton
                            iconName='trash'
                            title={`Delete ${noteType}`}
                            onClick={() => onDelete(val)}
                            className=''
                          />
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default ObservationsList;
